import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Auth.css';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogleCallback = useCallback((sessionToken, email, name, credits) => {
    console.log('Google OAuth callback triggered');
    setLoading(true);
    setError('');
    
    try {
      // Store session token and user data
      localStorage.setItem('session_token', sessionToken);
      
      // Navigate to try-on page
      const from = location.state?.from?.pathname || '/tryon';
      navigate(from, { replace: true });
      
      // Reload to update auth context
      window.location.reload();
    } catch (err) {
      console.error('Google login exception:', err);
      setError('An error occurred during Google login. Please try again.');
      setLoading(false);
    }
  }, [navigate, location]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const sessionToken = params.get('session_token');
      const error = params.get('error');
      
      if (error) {
        setError('Google login failed. Please try again.');
        // Clear hash
        window.history.replaceState(null, '', window.location.pathname);
      } else if (sessionToken) {
        const email = params.get('email');
        const name = params.get('name');
        const credits = params.get('credits');
        console.log('Session token found in URL hash');
        handleGoogleCallback(sessionToken, email, name, credits);
      }
    }
  }, [handleGoogleCallback]);

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/tryon';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleGoogleLogin = () => {
    // Redirect to backend OAuth endpoint
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${backendUrl}/api/auth/google/login`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      const from = location.state?.from?.pathname || '/tryon';
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className='auth-page'>
      <div className='auth-container'>
        <div className='auth-header'>
          <h1 className='auth-logo' onClick={() => navigate('/')}>FITTTY.AI</h1>
          <h2>Welcome Back</h2>
          <p>Sign in to continue your virtual try-on experience</p>
        </div>

        {error && <div className='auth-error'>{error}</div>}

        <button className='google-btn' onClick={handleGoogleLogin} disabled={loading}>
          <img src='https://www.google.com/favicon.ico' alt='Google' />
          Continue with Google
        </button>

        <div className='auth-divider'>
          <span>OR</span>
        </div>

        <form onSubmit={handleEmailLogin} className='auth-form'>
          <div className='form-group'>
            <label>Email</label>
            <input
              type='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder='your@email.com'
              required
              disabled={loading}
            />
          </div>

          <div className='form-group'>
            <label>Password</label>
            <input
              type='password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder='••••••••'
              required
              disabled={loading}
            />
          </div>

          <button type='submit' className='auth-submit-btn' disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className='auth-footer'>
          <p>
            Don't have an account?{' '}
            <Link to='/signup'>Sign up</Link>
          </p>
        </div>

        <div className='auth-info'>
          <p>🎁 New users get 10 free try-on credits!</p>
        </div>
      </div>
    </div>
  );
};

export default Login;